import React, { useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";

/**
 * ProjectCard
 * Two layouts share this component:
 *   - featured: large, asymmetric, used for the first project
 *   - default: tighter, sits in a 2-column grid
 */
export const ProjectCard = ({ project, featured = false, index = 0 }) => {
  const [activeImage, setActiveImage] = useState(0);

  if (featured) {
    return (
      <motion.article
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-100px" }}
        transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
        data-testid={`project-card-${project.slug}`}
        className="group relative grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center border-t border-neutral-900 pt-12 md:pt-16"
      >
        {/* Image gallery */}
        <div className="lg:col-span-7 order-2 lg:order-1">
          <div className="relative aspect-[16/11] overflow-hidden bg-neutral-950 border border-neutral-900">
            {project.images.map((src, i) => (
              <motion.img
                key={src + i}
                src={src}
                alt={`${project.title} — view ${i + 1}`}
                loading="lazy"
                initial={{ opacity: 0 }}
                animate={{ opacity: activeImage === i ? 1 : 0 }}
                transition={{ duration: 0.6 }}
                className="absolute inset-0 w-full h-full object-cover"
                data-testid={`project-${project.slug}-image-${i}`}
              />
            ))}
            <div className="absolute inset-0 bg-black/10 pointer-events-none" />
            <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between">
              <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-300 bg-black/60 backdrop-blur px-2 py-1">
                Featured · {project.year}
              </span>
              {project.images.length > 1 && (
                <div className="flex gap-1.5">
                  {project.images.map((_, i) => (
                    <button
                      key={i}
                      aria-label={`Show image ${i + 1}`}
                      onClick={() => setActiveImage(i)}
                      data-testid={`project-${project.slug}-gallery-dot-${i}`}
                      className={`h-1.5 w-6 transition-colors ${
                        activeImage === i ? "bg-neutral-100" : "bg-neutral-700"
                      }`}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Copy */}
        <div className="lg:col-span-5 order-1 lg:order-2 space-y-6">
          <div className="flex items-center gap-3">
            <span className="text-mono-label">
              {String(index + 1).padStart(2, "0")}
            </span>
            <span className="text-mono-label">{project.role}</span>
          </div>

          <h3 className="font-serif text-3xl md:text-4xl leading-[1.05] tracking-tight text-neutral-100">
            {project.title}
          </h3>

          <p className="font-serif italic text-lg md:text-xl text-neutral-300 leading-snug">
            {project.tagline}
          </p>

          <p className="text-neutral-400 leading-relaxed">{project.description}</p>

          <ul className="space-y-2 pt-2">
            {project.features.map((f) => (
              <li
                key={f}
                className="flex items-start gap-3 text-sm text-neutral-300"
              >
                <span className="text-neutral-600 font-mono text-xs mt-1.5">▍</span>
                <span>{f}</span>
              </li>
            ))}
          </ul>

          <div className="flex flex-wrap gap-2 pt-2">
            {project.stack.map((t) => (
              <span
                key={t}
                className="border border-neutral-800 px-3 py-1 font-mono text-[11px] uppercase tracking-wider text-neutral-300"
              >
                {t}
              </span>
            ))}
          </div>

          <div className="pt-4">
            <Link
              to={`/work/${project.slug}`}
              data-testid={`project-${project.slug}-detail-link`}
              className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-neutral-400 hover:text-neutral-100 border border-neutral-800 hover:border-neutral-100 px-4 py-2.5 transition-colors duration-300"
            >
              View Case Study <ArrowUpRight size={12} />
            </Link>
          </div>
        </div>
      </motion.article>
    );
  }

  // Default card
  return (
    <motion.article
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1], delay: index * 0.05 }}
      data-testid={`project-card-${project.slug}`}
      className="group relative flex flex-col border border-neutral-900 bg-[#0e0e0e] hover:border-neutral-700 transition-colors duration-500"
    >
      <Link
        to={`/work/${project.slug}`}
        data-testid={`project-${project.slug}-card-link`}
        className="relative aspect-[16/10] overflow-hidden bg-neutral-950 block"
      >
        <img
          src={project.images[0]}
          alt={project.title}
          loading="lazy"
          className="absolute inset-0 w-full h-full object-cover scale-100 group-hover:scale-[1.04] transition-transform duration-[1200ms] ease-out"
          data-testid={`project-${project.slug}-image-0`}
        />
        <div className="absolute inset-0 bg-black/15 pointer-events-none" />
        <div className="absolute top-4 left-4 right-4 flex items-center justify-between">
          <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-300 bg-black/60 backdrop-blur px-2 py-1">
            {project.year}
          </span>
          <ArrowUpRight
            size={16}
            className="text-neutral-300 opacity-0 group-hover:opacity-100 transition-opacity"
          />
        </div>
      </Link>

      <div className="p-7 md:p-8 flex flex-col gap-4 flex-1">
        <div className="flex items-center gap-3">
          <span className="text-mono-label">
            {String(index + 1).padStart(2, "0")}
          </span>
          <span className="text-mono-label">{project.role}</span>
        </div>

        <Link to={`/work/${project.slug}`}>
          <h3 className="font-serif text-2xl md:text-[1.6rem] leading-[1.1] tracking-tight text-neutral-100 hover:text-neutral-300 transition-colors">
            {project.title}
          </h3>
        </Link>

        <p className="font-serif italic text-base md:text-lg text-neutral-300 leading-snug">
          {project.tagline}
        </p>

        <p className="text-sm text-neutral-400 leading-relaxed flex-1">
          {project.description}
        </p>

        <ul className="space-y-1.5 pt-1">
          {project.features.slice(0, 3).map((f) => (
            <li
              key={f}
              className="flex items-start gap-3 text-sm text-neutral-400"
            >
              <span className="text-neutral-700 font-mono text-xs mt-1.5">▍</span>
              <span>{f}</span>
            </li>
          ))}
        </ul>

        <div className="flex flex-wrap gap-2 pt-3 mt-auto">
          {project.stack.map((t) => (
            <span
              key={t}
              className="border border-neutral-800 px-2.5 py-1 font-mono text-[10px] uppercase tracking-wider text-neutral-300"
            >
              {t}
            </span>
          ))}
        </div>

        <Link
          to={`/work/${project.slug}`}
          data-testid={`project-${project.slug}-detail-link`}
          className="mt-2 inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500 hover:text-neutral-100 transition-colors duration-300"
        >
          View Details <ArrowUpRight size={10} />
        </Link>
      </div>
    </motion.article>
  );
};

export default ProjectCard;
